import React, { useState } from 'react';
import {
  FiUsers,
  FiSearch,
  FiFilter,
  FiChevronLeft,
  FiChevronRight,
  FiAward,
  FiDollarSign,
  FiCheckCircle,
  FiXCircle,
  FiClock,
  FiTrash2,
  FiAlertCircle,
  FiSquare,
  FiCheckSquare,
  FiLogIn,
} from 'react-icons/fi';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { adminApi } from '../../api';
import { Loader } from '../Common/Loader';
import { ErrorDisplay } from '../Common/ErrorDisplay';
import { User } from '../../types';
import { useApi } from '../../hooks/useApi';
import { showToast } from '../../utils/toast';
import { IoStatsChartSharp } from "react-icons/io5";
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../stores';
import { socketService } from '../../services/socketService';


// Extended User type for admin dashboard with subscription info
interface UserWithSubscription extends User {
  subscriptionStatus?: 'active' | 'expired' | 'none' | 'pending';
  subscription?: {
    status: string;
    isActive: boolean;
    endDate: string;
    daysRemaining: number;
  };
  totalProfitLoss?: number;
}

interface AdminDashboardProps {
  refresh: () => void;
}

const AdminDashboard: React.FC<AdminDashboardProps> = ({ refresh }) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { startImpersonation } = useAuthStore();
  const [page, setPage] = useState(1);
  const [roleFilter, setRoleFilter] = useState<string>('');
  const [subscriptionFilter, setSubscriptionFilter] = useState<string>('');
  const [searchQuery, setSearchQuery] = useState('');
  /** Search string sent to the API; only updates when the user clicks Search (or presses Enter). */
  const [appliedSearch, setAppliedSearch] = useState('');
  const [selectedUser, setSelectedUser] = useState<UserWithSubscription | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedUsers, setSelectedUsers] = useState<Set<string>>(new Set());
  const [showBulkDeleteModal, setShowBulkDeleteModal] = useState(false);
  const [loginAsUserId, setLoginAsUserId] = useState<string | null>(null);

  const applyUserSearch = () => {
    const t = searchQuery.trim();
    setAppliedSearch(t);
    setPage(1);
  };

  const trimmedSearch = appliedSearch.trim();
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['admin', 'users', page, roleFilter, subscriptionFilter, trimmedSearch],
    queryFn: () => adminApi.getAllUsers({
      page,
      limit: 10,
      role: roleFilter || undefined,
      subscription: subscriptionFilter || undefined,
      search: trimmedSearch || undefined,
    }),
  });

  const { data: statsData } = useQuery({
    queryKey: ['admin', 'stats'],
    queryFn: () => adminApi.getStats(),
    refetchOnWindowFocus: true,
    staleTime: 30 * 1000, 
  });

  const users: UserWithSubscription[] = data?.users || [];
  const pagination = data?.pagination;
  const stats = statsData?.stats || {
    totalUsers: 0,
    mastersCount: 0,
    activeSubscriptions: 0,
    totalBalance: 0
  };

  const deleteUserMutation = useApi.useDeleteUser({
    onSuccess: (data) => {
      showToast('success', data.message || 'User deleted successfully');
      setShowDeleteModal(false);
      setSelectedUser(null);
      refetch();
    },
    onError: (err: any) => {
      showToast('error', err.response?.data?.error || 'Failed to delete user');
    },
  });

  const approveFollowerMutation = useApi.useApproveFollower({
    onSuccess: (data) => {
      showToast('success', data.message || 'Account approved');
      refetch();
    },
    onError: (err: any) => {
      showToast('error', err.response?.data?.error || 'Failed to approve account');
    },
  });

  const rejectFollowerMutation = useApi.useRejectFollower({
    onSuccess: (data) => {
      showToast('success', data.message || 'Account rejected');
      refetch();
    },
    onError: (err: any) => {
      showToast('error', err.response?.data?.error || 'Failed to reject account');
    },
  });

  const handleDeleteUser = (user: UserWithSubscription) => {
    setSelectedUser(user);
    setShowDeleteModal(true);
  };

  const confirmDeleteUser = () => {
    if (!selectedUser || !selectedUser._id) return;
    deleteUserMutation.mutate(selectedUser._id);
  };

  // Bulk delete handlers
  const handleSelectUser = (userId: string) => {
    setSelectedUsers(prev => {
      const newSet = new Set(prev);
      if (newSet.has(userId)) {
        newSet.delete(userId);
      } else {
        newSet.add(userId);
      }
      return newSet;
    });
  };

  const handleSelectAll = () => {
    const deletableUsers = users.filter(u => u.role !== 'admin' && u._id).map(u => u._id as string);
    if (selectedUsers.size === deletableUsers.length) {
      setSelectedUsers(new Set());
    } else {
      setSelectedUsers(new Set(deletableUsers));
    }
  };

  const handleBulkDelete = () => {
    if (selectedUsers.size === 0) return;
    setShowBulkDeleteModal(true);
  };

  const handleLoginAs = async (user: UserWithSubscription) => {
    if (!user._id) return;
    setLoginAsUserId(user._id);
    try {
      const data = await adminApi.loginAs(user._id);
      if (data.token && data.user) {
        startImpersonation(data.token, data.user);
        // Drop cached API data from the previous user so dashboard/masters load without a manual reload
        queryClient.clear();
        queryClient.setQueryData(['auth', 'me'], { success: true, user: data.user });
        localStorage.removeItem('selectedBrokerId');
        socketService.disconnect();
        showToast('success', `Logged in as ${data.user.name}`);
        navigate('/dashboard');
      }
    } catch (err: any) {
      showToast('error', err.response?.data?.error || 'Failed to log in as user');
    } finally {
      setLoginAsUserId(null);
    }
  };

  const confirmBulkDelete = async () => {
    const userIds = Array.from(selectedUsers);
    let successCount = 0;
    let errorCount = 0;

    for (const userId of userIds) {
      try {
        await adminApi.deleteUser(userId);
        successCount++;
      } catch (error: any) {
        errorCount++;
        console.error(`Failed to delete user ${userId}:`, error);
      }
    }

    setShowBulkDeleteModal(false);
    setSelectedUsers(new Set());
    refetch();
    // Invalidate stats query to refresh dashboard stats
    queryClient.invalidateQueries({ queryKey: ['admin', 'stats'] });

    if (successCount > 0) {
      showToast('success', `Successfully deleted ${successCount} user${successCount > 1 ? 's' : ''}`);
    }
    if (errorCount > 0) {
      showToast('error', `Failed to delete ${errorCount} user${errorCount > 1 ? 's' : ''}`);
    }
  };

  const pillBase =
    'inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium whitespace-nowrap border';
  const dot = (color: string) => <span className={`h-1.5 w-1.5 rounded-full ${color}`} />;
  const actionBtn =
    'h-8 w-8 shrink-0 inline-flex items-center justify-center rounded-lg border transition-colors disabled:opacity-50 disabled:cursor-not-allowed';

  const getRoleBadge = (role?: string | null) => {
    const colors = {
      admin: 'bg-primary/10 text-primary border-primary/25',
      master: 'bg-primary/10 text-primary border-primary/25',
      follower: 'bg-surface-elevated text-foreground/80 border-border',
      master_pending: 'bg-warning/10 text-warning border-warning/25',
    };
    const raw = typeof role === 'string' ? role.trim() : '';
    const badgeClass =
      raw && raw in colors ? colors[raw as keyof typeof colors] : colors.follower;
    const label =
      raw === 'master_pending'
        ? 'Pending Master'
        : raw
          ? raw.charAt(0).toUpperCase() + raw.slice(1).replace(/_/g, ' ')
          : 'Unknown';
    return (
      <span className={`${pillBase} ${badgeClass}`}>
        {label}
      </span>
    );
  };

  const getAccountStatusBadge = (user: UserWithSubscription) => {
    if (user.role !== 'follower') return null;
    const status = user.status || 'active';
    const styles: Record<string, { pill: string; dot: string }> = {
      active: { pill: 'bg-success/10 text-success border-success/25', dot: 'bg-success' },
      pending: { pill: 'bg-warning/10 text-warning border-warning/25', dot: 'bg-warning' },
      rejected: { pill: 'bg-danger/10 text-danger border-danger/25', dot: 'bg-danger' },
    };
    const labels: Record<string, string> = {
      active: 'Approved',
      pending: 'Pending',
      rejected: 'Rejected',
    };
    const style = styles[status] || styles.active;
    return (
      <span className={`${pillBase} ${style.pill}`}>
        {dot(style.dot)}
        {labels[status] || 'Approved'}
      </span>
    );
  };

  const getSubscriptionBadge = (user: UserWithSubscription) => {
    if (!user.subscriptionStatus || user.subscriptionStatus === 'none') {
      return <span className="text-sm text-muted whitespace-nowrap">No subscription</span>;
    }

    if (user.subscriptionStatus === 'active') {
      const days = user.subscription?.daysRemaining || 0;
      // Very long remaining periods are admin-granted lifetime access
      if (days > 3650) {
        return (
          <span className={`${pillBase} bg-primary/10 text-primary border-primary/25`}>
            <FiAward className="h-3 w-3" />
            Lifetime
          </span>
        );
      }
      return (
        <span className={`${pillBase} bg-success/10 text-success border-success/25`}>
          <FiClock className="h-3 w-3" />
          {days} day{days === 1 ? '' : 's'} left
        </span>
      );
    }

    if (user.subscriptionStatus === 'expired') {
      return (
        <span className={`${pillBase} bg-danger/10 text-danger border-danger/25`}>
          <FiXCircle className="h-3 w-3" />
          Expired
        </span>
      );
    }

    return (
      <span className={`${pillBase} bg-warning/10 text-warning border-warning/25`}>
        <FiClock className="h-3 w-3" />
        Pending
      </span>
    );
  };

  const formatMoney = (value: number) =>
    value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  const getInitials = (name?: string | null, email?: string | null) => {
    const source = (name || email || '?').trim();
    const parts = source.split(/\s+/).filter(Boolean);
    return ((parts[0]?.[0] || '') + (parts.length > 1 ? parts[parts.length - 1][0] : '')).toUpperCase() || '?';
  };

  const handleClearFilters = () => {
    setRoleFilter('');
    setSubscriptionFilter('');
    setSearchQuery('');
    setAppliedSearch('');
    setPage(1);
  };

  if (isLoading) {
    return <Loader message="Loading users..." />;
  }

  if (error) {
    return <ErrorDisplay error="Failed to load users" />;
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="min-w-0">
          <h2 className="lg:text-4xl sm:text-3xl text-2xl title-page">
            Admin Dashboard
          </h2>
          <p className="text-muted lg:mt-2 mt-1 lg:text-lg text-[14px]">Manage platform users and system operations</p>
        </div>
      </div>

      {/* Stats Cards – first 3 equal width (slightly wider), 4th card wider */}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-9 gap-4">
        <div className="card lg:col-span-2">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-primary/10 rounded-lg">
              <FiUsers className="h-6 w-6 text-primary" />
            </div>
            <div>
              <p className="text-sm text-muted">Total Users</p>
              <p className="lg:text-xl text-xl font-bold text-foreground">{stats.totalUsers}</p>
            </div>
          </div>
        </div>
        <div className="card lg:col-span-2">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-primary/10 rounded-lg">
              <FiAward className="h-6 w-6 text-primary" />
            </div>
            <div>
              <p className="text-sm text-muted">Masters</p>
              <p className="lg:text-xl text-xl font-bold text-foreground">
                {stats.mastersCount}
              </p>
            </div>
          </div>
        </div>
        <div className="card lg:col-span-2">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-success/10 rounded-lg">
              <FiCheckCircle className="h-6 w-6 text-success" />
            </div>
            <div>
              <p className="text-sm text-muted whitespace-nowrap">Active Subscriptions</p>
              <p className="lg:text-xl text-xl font-bold text-foreground">
                {stats.activeSubscriptions}
              </p>
            </div>
          </div>
        </div>
        <div
          className={`card ${stats.totalBalance > 1000 ? "lg:col-span-3" : "lg:col-span-2"
            }`}
        >
          <div className="flex items-center gap-3">
            <div className="p-2 bg-primary/10 rounded-lg">
              <FiDollarSign className="h-6 w-6 text-primary" />
            </div>
            <div>
              <p className="text-sm text-muted">Total Balance</p>
              <p className="lg:text-xl text-xl font-bold text-foreground">
                ${stats.totalBalance.toLocaleString()}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters and Search */}
      <div className="card p-3 sm:p-6">
        <div className="flex flex-col md:flex-row gap-2 md:gap-4">
          {/* Search */}
          <div className="flex-1 flex flex-row gap-2">
            <div className="flex-1 relative min-w-0">
              <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted h-4 w-4 md:h-5 md:w-5" />
              <input
                type="text"
                placeholder="Search by name or email..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    applyUserSearch();
                  }
                }}
                className="w-full h-10 md:h-auto pl-9 md:pl-10 pr-3 md:pr-4 py-2 text-sm md:text-base bg-surface border border-border rounded-lg text-foreground placeholder-muted focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>
            <button
              type="button"
              onClick={applyUserSearch}
              aria-label="Search"
              className="btn-primary shrink-0 h-10 w-10 p-0 sm:h-auto sm:w-auto sm:px-4 sm:py-2 flex items-center justify-center gap-2 whitespace-nowrap"
            >
              <FiSearch className="h-4 w-4" />
              <span className="hidden sm:inline">Search</span>
            </button>
          </div>

          <div className="grid grid-cols-2 gap-2 md:flex md:gap-4">
          {/* Role Filter */}
          <select
            value={roleFilter}
            onChange={(e) => {
              setRoleFilter(e.target.value);
              setPage(1);
            }}
            className="w-full min-w-0 h-10 md:h-auto md:w-auto px-3 md:px-4 py-2 text-sm md:text-base truncate bg-surface border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
          >
            <option value="">All Roles</option>
            <option value="admin">Admin</option>
            <option value="master">Master</option>
            <option value="follower">Follower</option>
            <option value="master_pending">Pending Master</option>
          </select>

          {/* Subscription Filter */}
          <select
            value={subscriptionFilter}
            onChange={(e) => {
              setSubscriptionFilter(e.target.value);
              setPage(1);
            }}
            className="w-full min-w-0 h-10 md:h-auto md:w-auto px-3 md:px-4 py-2 text-sm md:text-base truncate bg-surface border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
          >
            <option value="">All Subscriptions</option>
            <option value="active">Active</option>
            <option value="expired">Expired</option>
            <option value="none">No Subscription</option>
          </select>

          {/* Clear Filters */}
          {(roleFilter || subscriptionFilter || searchQuery || appliedSearch) && (
            <button
              onClick={handleClearFilters}
              className="col-span-2 h-10 md:h-auto px-4 py-2 text-sm md:text-base bg-surface border border-border rounded-lg text-foreground hover:bg-surface-elevated transition-colors flex items-center justify-center gap-2"
            >
              <FiFilter className="h-4 w-4" />
              Clear
            </button>
          )}
          </div>
        </div>
      </div>

      {/* Bulk Actions Bar */}
      {selectedUsers.size > 0 && (
        <div className="card bg-primary/5 border border-primary/20">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <span className="text-sm font-semibold text-primary">
                {selectedUsers.size} user{selectedUsers.size > 1 ? 's' : ''} selected
              </span>
            </div>
            <button
              onClick={handleBulkDelete}
              className="px-4 py-2 bg-danger hover:opacity-90 text-white font-semibold rounded-lg transition-colors flex items-center gap-2"
            >
              <FiTrash2 className="h-4 w-4" />
              Delete Selected
            </button>
          </div>
        </div>
      )}

      {/* Users Table */}
      <div className="card overflow-x-auto min-w-0 !p-0">
        <div className="min-w-full table-scroll">
          <table className="w-full min-w-[1000px] lg:min-w-0">
            <thead>
              <tr className="border-b border-border bg-surface-elevated/40 [&>th]:py-3 [&>th]:px-4 [&>th]:text-[11px] [&>th]:font-semibold [&>th]:uppercase [&>th]:tracking-wider [&>th]:text-muted [&>th]:whitespace-nowrap">
                <th className="text-left w-12">
                  <button
                    onClick={handleSelectAll}
                    className="p-1 hover:bg-surface-elevated rounded transition-colors"
                    title="Select All"
                  >
                    {users.filter(u => u.role !== 'admin').length > 0 &&
                      selectedUsers.size === users.filter(u => u.role !== 'admin').length ? (
                      <FiCheckSquare className="h-[18px] w-[18px] text-primary" />
                    ) : (
                      <FiSquare className="h-[18px] w-[18px] text-muted" />
                    )}
                  </button>
                </th>
                <th className="text-left">User</th>
                <th className="text-left">Role</th>
                <th className="text-left">Status</th>
                {/* <th className="text-left">Trading Type</th> */}
                <th className="text-left">Subscription</th>
                <th className="text-right">Balance</th>
                <th className="text-right">P&amp;L</th>
                <th className="text-left">Broker</th>
                <th className="text-left">Joined</th>
                <th className="text-left">Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.filter(u => u.role !== 'admin').length === 0 ? (
                <tr>
                  <td colSpan={10} className="text-center py-14">
                    <FiUsers className="h-8 w-8 text-muted mx-auto mb-2" />
                    <p className="text-sm text-muted">No users found</p>
                  </td>
                </tr>
              ) : (
                users.filter(u => u.role !== 'admin').map((user) => {
                  const isSelected = selectedUsers.has(user._id as string);
                  const balance = user.hasBrokerageConnected ? (user.accountBalance ?? 0) : null;
                  const pnl = user.totalProfitLoss || 0;
                  const isMasterRole = user.role === 'master' || user.role === 'master_pending';
                  return (
                  <tr
                    key={user._id}
                    className={`border-b border-border last:border-b-0 transition-colors [&>td]:py-3.5 [&>td]:px-4 [&>td]:align-middle ${
                      isSelected ? 'bg-primary/5' : 'hover:bg-surface-elevated/60'
                    }`}
                  >
                    <td>
                      {user.role !== 'admin' && user._id && (
                        <button
                          onClick={() => handleSelectUser(user._id as string)}
                          className="p-1 hover:bg-surface-elevated rounded transition-colors"
                        >
                          {isSelected ? (
                            <FiCheckSquare className="h-[18px] w-[18px] text-primary" />
                          ) : (
                            <FiSquare className="h-[18px] w-[18px] text-muted" />
                          )}
                        </button>
                      )}
                    </td>
                    <td>
                      <div className="flex items-center gap-3 min-w-[14rem]">
                        <div
                          className={`h-9 w-9 shrink-0 rounded-full flex items-center justify-center text-xs font-bold ${
                            isMasterRole ? 'bg-primary/15 text-primary' : 'bg-surface-elevated text-foreground/80'
                          }`}
                        >
                          {getInitials(user.name, user.email)}
                        </div>
                        <div className="min-w-0">
                          <p className="font-semibold text-foreground truncate">{user.name}</p>
                          <p className="text-xs text-muted truncate">{user.email}</p>
                        </div>
                      </div>
                    </td>
                    <td>
                      {getRoleBadge(user.role)}
                    </td>
                    <td>
                      {getAccountStatusBadge(user) || <span className="text-muted">—</span>}
                    </td>
                    {/* <td className="py-3 px-4">
                      {user.role === 'master' && user.tradingType ? (
                        <span className={`inline-flex items-center gap-1 px-2.5 py-1 text-xs font-semibold rounded-full border ${user.tradingType === 'swing'
                          ? 'bg-primary/10 text-primary border-primary/20'
                          : 'bg-primary/10 text-primary border-primary/20'
                          }`}>
                          {user.tradingType === 'swing' ? 'Swing' : 'Day'}
                        </span>
                      ) : (
                        <span className="text-xs text-muted">-</span>
                      )}
                    </td> */}
                    <td>
                      {getSubscriptionBadge(user)}
                    </td>
                    <td className="text-right tabular-nums whitespace-nowrap">
                      {balance == null ? (
                        <span className="text-muted">—</span>
                      ) : (
                        <span className="font-semibold text-foreground">${formatMoney(balance)}</span>
                      )}
                    </td>
                    <td className="text-right tabular-nums whitespace-nowrap">
                      <span
                        className={`font-semibold ${
                          pnl > 0 ? 'text-success' : pnl < 0 ? 'text-danger' : 'text-muted'
                        }`}
                      >
                        {pnl > 0 ? '+' : pnl < 0 ? '−' : ''}${formatMoney(Math.abs(pnl))}
                      </span>
                    </td>
                    <td>
                      {user?.hasBrokerageConnected ? (
                        <span className={`${pillBase} bg-success/10 text-success border-success/25`}>
                          {dot('bg-success')}
                          Connected
                        </span>
                      ) : (
                        <span className={`${pillBase} bg-surface-elevated text-muted border-border`}>
                          {dot('bg-muted')}
                          Not connected
                        </span>
                      )}
                    </td>
                    <td className="whitespace-nowrap">
                      <span className="text-sm text-muted">
                        {user.createdAt
                          ? new Date(user.createdAt).toLocaleDateString(undefined, {
                              month: 'short',
                              day: 'numeric',
                              year: 'numeric',
                            })
                          : '—'}
                      </span>
                    </td>
                    <td className="whitespace-nowrap">
                      {/* Fixed order (stats · login · delete) so icons line up; approve/reject get their own slot */}
                      <div className="flex items-center gap-1.5">
                        {user.role !== 'admin' && (
                          <button
                            onClick={() => navigate(`/dashboard/statistics?userid=${user._id}`)}
                            className={`${actionBtn} text-sky-400 bg-sky-500/10 border-sky-500/20 hover:bg-sky-500/20`}
                            title="Check Statistics"
                          >
                            <IoStatsChartSharp className="h-4 w-4" />
                          </button>
                        )}
                        <button
                          onClick={() => handleLoginAs(user)}
                          disabled={loginAsUserId !== null}
                          className={`${actionBtn} text-primary bg-primary/10 border-primary/20 hover:bg-primary/20`}
                          title="Login as this user"
                        >
                          <FiLogIn className="h-4 w-4" />
                        </button>
                        {user.role !== 'admin' && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteUser(user);
                            }}
                            className={`${actionBtn} text-danger bg-danger/10 border-danger/20 hover:bg-danger/20`}
                            title="Delete User"
                          >
                            <FiTrash2 className="h-4 w-4" />
                          </button>
                        )}
                        <div className="flex items-center gap-1.5 w-[4.375rem] pl-1.5 border-l border-border">
                          {user.role === 'follower' && user.status !== 'active' && (
                            <button
                              onClick={() => approveFollowerMutation.mutate(user._id as string)}
                              disabled={approveFollowerMutation.isPending || rejectFollowerMutation.isPending}
                              className={`${actionBtn} text-success bg-success/10 border-success/20 hover:bg-success/20`}
                              title={user.status === 'rejected' ? 'Re-approve account' : 'Approve account'}
                            >
                              <FiCheckCircle className="h-4 w-4" />
                            </button>
                          )}
                          {user.role === 'follower' && user.status !== 'rejected' && (
                            <button
                              onClick={() => {
                                if (user.status === 'active') {
                                  const confirmed = window.confirm(
                                    `Reject ${user.name}? This cancels their billing, stops copy trading immediately, and logs them out.`
                                  );
                                  if (!confirmed) return;
                                }
                                rejectFollowerMutation.mutate(user._id as string);
                              }}
                              disabled={approveFollowerMutation.isPending || rejectFollowerMutation.isPending}
                              className={`${actionBtn} text-warning bg-warning/10 border-warning/20 hover:bg-warning/20`}
                              title={user.status === 'active' ? 'Reject & suspend account' : 'Reject account'}
                            >
                              <FiXCircle className="h-4 w-4" />
                            </button>
                          )}
                        </div>
                      </div>
                    </td>
                  </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {pagination && pagination.pages > 1 && (
          <div className="flex flex-col sm:flex-row items-center justify-between gap-2 px-4 py-3 border-t border-border">
            <div className="text-sm text-muted text-center sm:text-left">
              Showing {(page - 1) * 10 + 1} to {Math.min(page * 10, pagination.total)} of {pagination.total} users
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
                className="p-2 bg-surface border border-border rounded-lg hover:bg-surface-elevated disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <FiChevronLeft className="h-5 w-5" />
              </button>
              <span className="px-4 py-2 text-sm font-medium text-foreground">
                Page {page} of {pagination.pages}
              </span>
              <button
                onClick={() => setPage(p => Math.min(pagination.pages, p + 1))}
                disabled={page === pagination.pages}
                className="p-2 bg-surface border border-border rounded-lg hover:bg-surface-elevated disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <FiChevronRight className="h-5 w-5" />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Delete User Modal */}
      {showDeleteModal && selectedUser && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-3 sm:p-4">
          <div className="bg-surface-elevated rounded-xl shadow-2xl max-w-md w-full max-h-[90vh] overflow-y-auto p-4 sm:p-6 border border-border">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-3 bg-danger/10 rounded-full">
                <FiAlertCircle className="h-6 w-6 text-danger" />
              </div>
              <h3 className="text-xl font-bold text-foreground">Delete User</h3>
            </div>

            <p className="text-muted mb-4">
              Are you sure you want to delete this user:{' '}
              <strong className="text-foreground break-anywhere">{selectedUser.name || selectedUser.email}</strong>?
            </p>

            <div className="bg-danger/5 border border-danger/20 rounded-lg p-4 mb-4">
              <p className="text-sm text-red-800 dark:text-red-300">
                <strong>Warning:</strong> This action cannot be undone. All user data, subscriptions, trades, and relationships will be permanently deleted.
              </p>
            </div>

            <div className="flex flex-col-reverse sm:flex-row gap-2 sm:gap-3">
              <button
                onClick={() => {
                  setShowDeleteModal(false);
                  setSelectedUser(null);
                }}
                className="flex-1 px-4 py-2 border-2 border-border text-foreground font-semibold rounded-lg hover:bg-surface transition-colors"
                disabled={deleteUserMutation.isPending}
              >
                Cancel
              </button>
              <button
                onClick={confirmDeleteUser}
                className="flex-1 px-4 py-2 bg-danger hover:opacity-90 text-white font-semibold rounded-lg transition-colors"
                disabled={deleteUserMutation.isPending}
              >
                {deleteUserMutation.isPending ? 'Deleting...' : 'Confirm Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Delete Modal */}
      {showBulkDeleteModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-3 sm:p-4">
          <div className="bg-surface-elevated rounded-xl shadow-2xl max-w-md w-full max-h-[90vh] overflow-y-auto p-4 sm:p-6 border border-border">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-3 bg-danger/10 rounded-full">
                <FiAlertCircle className="h-6 w-6 text-danger" />
              </div>
              <h3 className="text-xl font-bold text-foreground">Delete Selected Users</h3>
            </div>

            <p className="text-muted mb-4">
              Are you sure you want to <strong className="text-danger">permanently delete</strong> {selectedUsers.size} user{selectedUsers.size > 1 ? 's' : ''}?
            </p>

            <div className="bg-danger/5 border border-danger/20 rounded-lg p-4 mb-4">
              <p className="text-sm text-red-800 dark:text-red-300">
                <strong>Warning:</strong> This action cannot be undone. All user data, subscriptions, trades, and relationships will be permanently deleted.
              </p>
            </div>

            <div className="flex flex-col-reverse sm:flex-row gap-2 sm:gap-3">
              <button
                onClick={() => {
                  setShowBulkDeleteModal(false);
                }}
                className="flex-1 px-4 py-2 border-2 border-border text-foreground font-semibold rounded-lg hover:bg-surface transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={confirmBulkDelete}
                className="flex-1 px-4 py-2 bg-danger hover:opacity-90 text-white font-semibold rounded-lg transition-colors"
              >
                Delete {selectedUsers.size} User{selectedUsers.size > 1 ? 's' : ''}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;

